import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ProtectedRoute } from 'components';
import {
  ChatCostSummary,
  ChatList,
  EditChat,
  EditChatRag,
  FlowPicker,
  Login,
  ModelMetadataList,
  UserList,
} from 'pages/dashboard';
import DashboardLayout from 'pages/dashboard/DashboardLayout';
import Conversations from 'pages/dashboard/Conversations';
import { LoadBot } from 'pages/dashboard/LoadBot';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { landingPageUrl } from 'utils';
import { AuthProvider } from './contexts';
import './i18n'; // Initialize i18n
import './index.css';
import reportWebVitals from './reportWebVitals';
import { TooltipProvider } from 'components/shadcn/tooltip';
import { CreateChatForm } from 'pages/dashboard/CreateChatForm';
import NotFound from 'components/NotFound';
import AdminSettings from '@/pages/dashboard/AdminSettings';
import { WebConversation } from '@/components/WebConversation';

const queryClient = new QueryClient();

const LoadingScreenRemover = () => {
  React.useEffect(() => {
    const loader = document.getElementById('loading-screen');
    if (loader) {
      // NOTE: we use requestAnimationFrame to delay the gif removal
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            loader.style.transition = 'opacity 0.4s ease-out';
            loader.style.opacity = '0';
          }, 1000);
          setTimeout(() => {
            loader.style.display = 'none';
          }, 2000);
        });
      });
    }
  }, []);
  return null;
};

const rootElement = document.getElementById('root');
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);

  root.render(
    <React.StrictMode>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <Toaster
            toastOptions={{
              closeButton: true,
            }}
            icons={{
              success: <span className="text-lg">🎉</span>,
            }}
          />
          <TooltipProvider delayDuration={0}>
            <BrowserRouter basename={import.meta.env.VITE_PUBLIC_URL}>
              <LoadingScreenRemover />
              <Routes>
                <Route path="/:id" element={<WebConversation />} />

                <Route
                  path="/:id"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path=":action" element={<EditChat />} />
                  <Route path="edit/rag" element={<EditChatRag />} />
                  <Route path="conversations" element={<Conversations />} />
                </Route>

                <Route
                  path="/user"
                  element={
                    <ProtectedRoute>
                      <LoadBot />
                    </ProtectedRoute>
                  }
                />

                <Route path="login" element={<Login />} />
                <Route
                  path="admin"
                  element={
                    <ProtectedRoute requireAdmin>
                      <FlowPicker />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="chats"
                  element={
                    <ProtectedRoute requireAdmin>
                      <ChatList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute requireAdmin>
                      <UserList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="adminSettings"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="models"
                  element={
                    <ProtectedRoute requireAdmin>
                      <ModelMetadataList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="create"
                  element={
                    <ProtectedRoute requireAdmin>
                      <div className="p-4 max-w-md">
                        {/* TODO this form is bad */}
                        <CreateChatForm />
                      </div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="cost-summary/:id"
                  element={
                    <ProtectedRoute requireAdmin>
                      <ChatCostSummary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/"
                  Component={() => {
                    window.location.href = landingPageUrl;
                    return null;
                  }}
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </AuthProvider>
    </React.StrictMode>,
  );
}

reportWebVitals();
